import { addYears, isAfter, isSameDay, startOfDay } from 'date-fns';
import Decimal from 'decimal.js';
import { compact } from 'lodash-es';
import { OneTimeInStagesModel, StagesTerms, StagesValueType } from "../models/OneTimeInStagesModel";
import { RecurrencePeriodTypes } from "../models/service-billing";
import { ServiceCostType } from "../models/service-cost";
import { fromDateStampObject } from "../models/time";
import { BillingTriggerType } from "../types/api.model";
import { utils } from "../utils";
export const activeAgreementCannotModifiyTriggerTooltip = 'Billing trigger cannot be modified; to modify a trigger, create a new service and add it to the agreement.';
export const activeAgreementCannotModifiyOccurrenceTooltip = 'Billing occurrence cannot be modified; to modify an occurrence, create a new service and add it to the agreement.';
export const yearlyServicesCanOnlyBeBilledManuallyTooltip = 'Yearly services can only be billed manually';
export function makeInvoiceDateInfo(invoiceDraftDates, selectedDate) {
  if (!invoiceDraftDates.dates().length) {
    throw new Error('futureInvoicesDates.length is 0');
  }
  const actualSelectedDate = selectedDate || invoiceDraftDates.dates()[0];
  return {
    invoiceDraftDates,
    selectedDate: actualSelectedDate,
    isUpcomingSelected: isSameDay(actualSelectedDate, invoiceDraftDates.dates()[0])
  };
}
export function getCreditsWarning(totalCredits) {
  if (totalCredits.gt(0)) return {
    text: "Your client has ".concat(utils.formatDollarAmount(totalCredits), " in credit. To prevent credits from automatically being applied to invoices, disable them in the Billing Control.")
  };
  return null;
}
export const getMaxFutureInvoiceDate = now => addYears(startOfDay(now), 1);

// TODO(ahuvi): try to remove oneTimeInStages - we already merged IN_STAGES_OCCURENCE so it should be safe to do so(?)

export function serviceBillingTriggerOptions(_ref) {
  let {
    isFutureEffectiveDate,
    recurrencePeriod,
    pricingType,
    billingTrigger,
    isFromAgreement,
    isExistingService,
    featureFlags
  } = _ref;
  const {
    YEARLY_AUTOMATIC_SERVICE,
    BILLING_OCCURRENCE_INLINE_SELECT_SERVICE_INSTANCE
  } = featureFlags;
  const isOneTime = !recurrencePeriod;
  const isYearly = recurrencePeriod === RecurrencePeriodTypes.Yearly;
  const isVariablePrice = pricingType === ServiceCostType.Variable;
  const isFixedPrice = pricingType === ServiceCostType.Fixed;
  const isRangePrice = pricingType === ServiceCostType.Range;
  const isOneTimeManual = !recurrencePeriod && billingTrigger === BillingTriggerType.OneTimeManual;
  const isAmendment = isExistingService && isFromAgreement;
  const {
    BILLED_EFFECTIVE_DATE_TEXT
  } = featureFlags;
  const getBilledEffectiveDateText = () => {
    if (BILLED_EFFECTIVE_DATE_TEXT) {
      if (isFutureEffectiveDate) {
        return 'Automatic, on effective date';
      }
      if (isFromAgreement) {
        return 'Automatic';
      }
      return 'Automatic, on agreement approval';
    }
    if (isFromAgreement) {
      return 'Automatic';
    }
    return 'Automatic, on agreement approval';
  };
  const getBilledEffectiveDateSubtext = () => {
    if (BILLED_EFFECTIVE_DATE_TEXT) {
      if (isFutureEffectiveDate) {
        return 'The service will be charged on the effective date of the agreement without requiring any action on your end';
      }
      if (isFromAgreement) {
        return 'An invoice will be issued immediately once amendments are saved and payment will require client approval';
      }
      return 'An invoice will be sent on agreement approval without requiring any action on your end';
    }
    if (isFromAgreement) {
      return 'An invoice will be issued immediately once amendments are saved and payment will require client approval';
    }
    return 'An invoice will be sent on agreement approval without requiring any action on your end';
  };
  return compact([isFixedPrice && isOneTime && !(isAmendment && isOneTimeManual) && {
    value: 'approval',
    text: getBilledEffectiveDateText(),
    subtext: getBilledEffectiveDateSubtext()
  }, !isVariablePrice && !isRangePrice && !isOneTime && (YEARLY_AUTOMATIC_SERVICE || !isYearly || BILLING_OCCURRENCE_INLINE_SELECT_SERVICE_INSTANCE) && {
    disabled: isYearly && !YEARLY_AUTOMATIC_SERVICE,
    value: 'ongoing',
    text: 'Automatic, on schedule',
    subtext: 'Invoices will be automatically sent based on the billing period set in the agreement'
  }, isFixedPrice && {
    value: 'manualFixed',
    text: 'Manual',
    subtext: 'You will be required to trigger the invoice'
  }, !isFixedPrice && {
    value: 'manualVariable',
    text: 'Manual, pending data entry',
    subtext: 'You will be required to input the total in order to trigger the invoice'
  }]);
}
function isFutureEffectiveDate(agreementEffectiveDate, now) {
  const normalized = agreementEffectiveDate && !(agreementEffectiveDate instanceof Date) ? fromDateStampObject(agreementEffectiveDate) : agreementEffectiveDate;
  return !!normalized && isAfter(startOfDay(normalized), startOfDay(now));
}
export const stagesTermsTextMap = {
  [StagesTerms.AutoFirst]: 'First stage is automatically billed',
  [StagesTerms.AllManual]: 'Manually billed stages'
};
export function serviceBillingStagesTermsOptions(_ref2) {
  let {
    now,
    agreementEffectiveDate,
    isFreshlyAddedToApprovedAgreement
  } = _ref2;
  const autoInvoiceIssued = isFreshlyAddedToApprovedAgreement ? 'once amendments are saved' : isFutureEffectiveDate(agreementEffectiveDate, now) ? 'on the effective date of the agreement' : 'on agreement approval';
  return [{
    value: StagesTerms.AutoFirst,
    text: stagesTermsTextMap[StagesTerms.AutoFirst],
    subtext: "First stage will be automatically billed ".concat(autoInvoiceIssued, ". Remaining stages are billed manually, according to the service terms.")
  }, {
    value: StagesTerms.AllManual,
    text: stagesTermsTextMap[StagesTerms.AllManual],
    subtext: 'Stages require a manual trigger to initiate billing.'
  }];
}
export const makeInitialOneTimeInStages = () => OneTimeInStagesModel.make({
  valueType: StagesValueType.Percent,
  terms: StagesTerms.AutoFirst,
  stages: [OneTimeInStagesModel.makeStage(1, new Decimal(50)), OneTimeInStagesModel.makeStage(2, new Decimal(50))]
});
export function hasYearlyAutomaticBillingError(service, featureFlags) {
  const {
    YEARLY_AUTOMATIC_SERVICE
  } = featureFlags;
  const isYearly = service.billing.recurrencePeriod === RecurrencePeriodTypes.Yearly;
  const isAutomatic = service.billing.trigger === BillingTriggerType.Ongoing || service.billing.trigger === BillingTriggerType.OnApproval;
  return isYearly && !YEARLY_AUTOMATIC_SERVICE && isAutomatic;
}